import { unitConstants, units } from './constants';

export interface IUnitValue {
  unit: string;
  value: number;
}

export interface IAttributes {
  [key: string]: IUnitValue;
}

export const conversionMap: Record<string, string> = {
  [units.liters]: units.gallons,
  [units.milliLiters]: units.fluidOunces,
  [units.kiloGrams]: units.pounds,
  [units.grams]: units.ounces,
  [units.centimeters]: units.inches,
  [units.gallons]: units.liters,
  [units.fluidOunces]: units.milliLiters,
  [units.pounds]: units.kiloGrams,
  [units.ounces]: units.grams,
  [units.inches]: units.centimeters,
};

export class ConversionUtility {
  static toggleUnit(imperial: boolean, unit: string): string {
    let needsConversion = false;

    if (imperial) {
      if (
        ![units.fluidOunces, units.ounces, units.pounds, units.gallons].includes(unit)
      ) {
        needsConversion = true;
      }
    } else if (
      ![units.milliLiters, units.grams, units.kiloGrams, units.liters].includes(unit)
    ) {
      needsConversion = true;
    }

    return needsConversion ? conversionMap[unit] || unit : unit;
  }

  // inch-centimeter conversions
  static inchToCenti(inch: number): number {
    return inch * unitConstants.cmInInches;
  }

  static centiToInch(cm: number): number {
    return cm / unitConstants.cmInInches;
  }

  // pound-kilogram conversions
  static poundToKilo(lb: number): number {
    return lb / unitConstants.KiloInlb;
  }

  static kiloToPound(kg: number): number {
    return kg * unitConstants.KiloInlb;
  }

  // gram-ounce conversions
  static gramsToOunces(grams: number): number {
    return grams / unitConstants.OuncesInGrams;
  }

  static ouncesToGrams(ounces: number): number {
    return ounces * unitConstants.OuncesInGrams;
  }

  // gallon-liter conversions
  static gallonsToLiters(gallons: number): number {
    return gallons * unitConstants.GallonsInLiters;
  }

  static litersToGallons(liters: number): number {
    return liters / unitConstants.GallonsInLiters;
  }

  // fluid ounce-milliliter conversions
  static millilitersToFluidOunces(milliliters: number): number {
    return milliliters / unitConstants.FluidOuncesInMilliliters;
  }

  static fluidOuncesToMilliliters(fluidOunces: number): number {
    return fluidOunces * unitConstants.FluidOuncesInMilliliters;
  }

  // cubic conversions
  static cbmToCbf(cbm: number): number {
    return 35.3147 * cbm;
  }

  static cbfToCbm(cbf: number): number {
    return cbf / 35.3147;
  }

  // metric conversions
  static convertItemToMetric(attributes: IAttributes, toFixed = true): IAttributes {
    const temp: IAttributes = {};
    Object.keys(attributes || {}).forEach(key => {
      const { unit, value } = attributes[key];
      let convertedValue = value;

      if (['inch', units.inches].includes(unit)) {
        convertedValue = this.inchToCenti(value || 0);
        temp[key] = { unit: units.centimeters, value: convertedValue };
      } else if (unit === units.pounds) {
        convertedValue = this.poundToKilo(value || 0);
        temp[key] = { unit: units.kiloGrams, value: convertedValue };
      } else if (unit === units.fluidOunces) {
        convertedValue = this.fluidOuncesToMilliliters(value);
        temp[key] = { unit: units.milliLiters, value: convertedValue };
      } else if (unit === units.gallons) {
        convertedValue = this.gallonsToLiters(value);
        temp[key] = { unit: units.liters, value: convertedValue };
      } else {
        temp[key] = attributes[key];
      }

      if (toFixed && typeof temp[key].value === 'number') {
        temp[key].value = parseFloat(temp[key].value.toFixed(2));
      }
    });
    return temp;
  }

  // imperial conversions
  static convertItemToImperial(attributes: IAttributes, toFixed = true): IAttributes {
    const temp: IAttributes = {};
    Object.keys(attributes || {}).forEach(key => {
      const { unit, value } = attributes[key];
      let convertedValue = value;

      if (unit === units.centimeters) {
        convertedValue = this.centiToInch(value || 0);
        temp[key] = { unit: units.inches, value: convertedValue };
      } else if (unit === units.kiloGrams) {
        convertedValue = this.kiloToPound(value || 0);
        temp[key] = { unit: units.pounds, value: convertedValue };
      } else if (unit === units.milliLiters) {
        convertedValue = this.millilitersToFluidOunces(value);
        temp[key] = { unit: units.fluidOunces, value: convertedValue };
      } else if (unit === units.liters) {
        convertedValue = this.litersToGallons(value);
        temp[key] = { unit: units.gallons, value: convertedValue };
      } else {
        temp[key] = attributes[key];
      }

      if (toFixed && typeof temp[key].value === 'number') {
        temp[key].value = parseFloat(temp[key].value.toFixed(2));
      }
    });
    return temp;
  }

  static convertToPoundsAndOunces(weightInPounds: number): {
    pounds: number;
    ounces: number;
  } {
    const pounds = Math.floor(weightInPounds);
    const ounces = (weightInPounds - pounds) * 16;
    return {
      pounds,
      ounces: parseFloat(ounces.toFixed(2)),
    };
  }
}
